<template>
  <div
    class="emd-progress-bar"
    :style="progressBarStyles"
    v-bind="computedAttrs"
  >
    <template v-if="$slots.default">
      <slot />
    </template>
    <template v-else-if="showProgress">
      {{ computedProgress }}
    </template>
    <template v-else-if="showValue">
      {{ computedValue }}
    </template>
    <template v-else>
      {{ label }}
    </template>
  </div>
</template>

<script>
import { toFixed } from './number.utils'

import { getProgress, getPrecision, getMax, getValue } from './math.utils'

export default {
  name: 'EmdProgressBar',

  props: {
    value: {
      type: [String, Number],
      default: 0
    },
    max: {
      type: [String, Number],
      default: 100
    },
    color: {
      type: String,
      default: '#5691D6'
    },
    height: {
      type: [String, Number],
      default: '13px'
    },
    precision: {
      type: Number,
      default: 2
    },
    showValue: Boolean,
    showProgress: Boolean,
    hasCalculatedValue: Boolean,
    label: String
  },

  computed: {
    computedMax () {
      return getMax(this.max)
    },
    computedPrecision () {
      return getPrecision(this.precision)
    },
    computedValue () {
      return getValue(this.value, this.computedPrecision)
    },
    computedProgress () {
      return this.handleProgress()
    },
    computedAttrs () {
      return {
        role: 'progressbar',
        'aria-valuemin': '0',
        'aria-valuemax': this.computedMax.toString(),
        'aria-valuenow': this.computedValue
      }
    },
    progressBarStyles () {
      // Calculates the bar width based on the computed properties
      const calcWidth = 100 * (this.computedValue / this.computedMax)
      const width = calcWidth > 0 && calcWidth < 1 ? 1 : calcWidth
      return {
        backgroundColor: this.color,
        width: this.clamp(width, this.computedMax) + '%',
        height: toFixed(this.height) + 'px'
      }
    }
  },

  created () {
    if (this.$listeners && this.$listeners.emitterProgress) {
      const progress = this.handleProgress()
      this.$emit('emitterProgress', progress)
    }
  },

  methods: {
    handleProgress () {
      if (!this.hasCalculatedValue) {
        const progress =
        this.computedValue && this.computedValue.slice(-3) === '.00'
          ? this.computedValue.slice(0, -3)
          : this.computedValue

        return progress + '%'
      }

      const progress = getProgress(
        this.computedValue,
        this.computedMax,
        this.computedPrecision
      )

      return progress + '%'
    },
    // to easily clamp numbers between a min and a max value
    clamp (value, max, min = 0) {
      return Math.min(Math.max(value, min), max)
    }
  }
}
</script>

<style scoped>
.emd-progress-bar {
  width: 0%;
  border-radius: 4px;
  border: none;

  display: flex;
  align-items: center;
  justify-content: center;

  font-size: 0.75rem;
  line-height: 0;
  color: white;
  text-align: right;
  white-space: nowrap;

  transition: width 0.6s ease;
}

.emd-progress .emd-progress-bar {
  display: flex;
  overflow: hidden;
  border-radius: 0;
}

.emd-progress .emd-progress-bar:first-child {
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
}

.emd-progress .emd-progress-bar:last-child {
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
}
</style>
